/**
 * @file: MessageActionMenu.tsx
 *
 * The long-press popover card for a message bubble (the content rendered
 * inside the shared {@link HoldItem} overlay). A white rounded card with a
 * greyed message-preview header and a column of icon + label action rows:
 * 回复 (always), 复制 (text payloads), 编辑 (own plain-text within the edit
 * window), 撤回 (own — destructive, confirmed). Pure: the actions are
 * coordinator handlers passed in; `close` dismisses the overlay (it runs the
 * action AFTER the dismiss animation so the page is interactive again first).
 */

import { Typography } from '@euler/app/components/typography';
import { isWithinEditWindow } from '@euler/app/flows/chat/utils/editWindow';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useAlert } from '@euler/functions/ux/useAlert';
import { ChatMsg } from '@euler/model/chat';
import * as Clipboard from 'expo-clipboard';
import { Copy, Pencil, Reply, Trash2 } from 'lucide-react-native';
import { FC, ReactNode, memo } from 'react';
import { TouchableOpacity, View } from 'react-native';

const ICON_COLOR = '#3f3f46';
const LABEL_COLOR = '#18181b';
const DESTRUCTIVE_COLOR = '#ef4444';

export const MessageActionMenu: FC<{
  message: ChatMsg;
  isMine: boolean;
  onReply: (message: ChatMsg) => void;
  onEdit: (message: ChatMsg) => void;
  onDelete: (message: ChatMsg) => void;
  /** Dismiss the overlay; the optional callback runs after the dismiss. */
  close: (after?: () => void) => void;
}> = memo(({ message, isMine, onReply, onEdit, onDelete, close }) => {
  const { rpx } = useResponsiveContext();
  const alert = useAlert();

  const { payload } = message;
  const previewText =
    payload.type === 'text'
      ? payload.text
      : payload.type === 'markdown'
      ? payload.markdown
      : null;
  const canCopy = !!previewText;
  // Only the user's own plain-text messages are editable (markdown / rich
  // payloads aren't), and only inside the edit window.
  const canEdit =
    isMine && payload.type === 'text' && isWithinEditWindow(message.createdAt);
  const canDelete = isMine;

  const confirmDelete = () => {
    void alert({
      title: '撤回消息',
      message: '确定撤回这条消息吗？',
      buttons: [
        { text: '取消', style: 'cancel' },
        {
          text: '撤回',
          style: 'destructive',
          onPress: () => onDelete(message),
        },
      ],
    });
  };

  const iconSize = rpx(19);
  const rows: {
    key: string;
    label: string;
    icon: ReactNode;
    destructive?: boolean;
    onPress: () => void;
  }[] = [
    {
      key: 'reply',
      label: '回复',
      icon: <Reply color={ICON_COLOR} size={iconSize} strokeWidth={2} />,
      onPress: () => close(() => onReply(message)),
    },
  ];
  if (canCopy) {
    rows.push({
      key: 'copy',
      label: '复制',
      icon: <Copy color={ICON_COLOR} size={iconSize} strokeWidth={2} />,
      onPress: () =>
        close(() => {
          if (previewText) void Clipboard.setStringAsync(previewText);
        }),
    });
  }
  if (canEdit) {
    rows.push({
      key: 'edit',
      label: '编辑',
      icon: <Pencil color={ICON_COLOR} size={iconSize} strokeWidth={2} />,
      onPress: () => close(() => onEdit(message)),
    });
  }
  if (canDelete) {
    rows.push({
      key: 'delete',
      label: '撤回',
      destructive: true,
      icon: (
        <Trash2 color={DESTRUCTIVE_COLOR} size={iconSize} strokeWidth={2} />
      ),
      onPress: () => close(() => confirmDelete()),
    });
  }

  return (
    <View
      style={{
        width: rpx(216),
        backgroundColor: '#ffffff',
        borderRadius: rpx(18),
        paddingVertical: rpx(6),
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.18,
        shadowRadius: 16,
        elevation: 8,
      }}
    >
      {rows.map(row => (
        <TouchableOpacity
          key={row.key}
          activeOpacity={0.6}
          onPress={row.onPress}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: rpx(14),
            paddingHorizontal: rpx(16),
            paddingVertical: rpx(12),
          }}
        >
          {row.icon}
          <Typography.Text
            size={rpx(15)}
            color={row.destructive ? DESTRUCTIVE_COLOR : LABEL_COLOR}
            style={{ fontFamily: FontFamily.NotoSans.Medium }}
          >
            {row.label}
          </Typography.Text>
        </TouchableOpacity>
      ))}
    </View>
  );
});
